import { useState, useEffect} from 'react'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css' // Le style de l'éditeur
import { X, FileText, Save, Wand2 } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../../../services/api'
import { format } from 'date-fns'

// --- CONFIGURATION DES MODÈLES ---
// Les variables entre {{...}} seront remplacées automatiquement.


const sanitizeFieldName = (label) => {
    if (!label) return 'champ_inconnu';
    return label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
}

export default function DocumentGeneratorModal({ patient, onClose, onSuccess }) {
    const [title, setTitle] = useState('')
    const [content, setContent] = useState('')
    const [loading, setLoading] = useState(false)
    const [templates, setTemplates] = useState([])
    const [showHeader, setShowHeader] = useState(true) 

    
    // --- ÉTATS POUR LE PUBLIPOSTAGE DYNAMIQUE ---
    const [notes, setNotes] = useState([])
    const [formTemplates, setFormTemplates] = useState([])
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [selectedSourceNoteId, setSelectedSourceNoteId] = useState('')
    const [isGeneratingAI, setIsGeneratingAI] = useState(false)


    useEffect(() => {
        api.get('/api/library/documents').then(res => setTemplates(res.data))
        // On a besoin des notes du patient (pour trouver celles qui ont du form_data) et des modèles de formulaires
        api.get(`/api/notes/patient/${patient.id}`).then(res => setNotes(res.data))
        api.get('/api/forms/templates').then(res => setFormTemplates(res.data))
        
        // Restauration du brouillon
        const backupTitle = localStorage.getItem('bellablue_doc_title_backup')
        const backupContent = localStorage.getItem('bellablue_doc_content_backup')
        
        if ((backupTitle || backupContent) && window.confirm("⚠️ Un document non sauvegardé a été détecté. Voulez-vous le restaurer ?")) {
            if (backupTitle) setTitle(backupTitle)
            if (backupContent) setContent(backupContent)
        }
    },[])

    // Sauvegarde automatique à chaque frappe
    useEffect(() => {
        if (title) localStorage.setItem('bellablue_doc_title_backup', title)
        if (content) localStorage.setItem('bellablue_doc_content_backup', content)
    },[title, content])


    // Sélectionne le modèle (mais ne remplace pas tout de suite s'il faut une source)
    const handleSelectTemplate = (tpl) => {
        if (tpl.id === 'blank') {
            setSelectedTemplate(null);
            setSelectedSourceNoteId('');
            setContent('');
            setTitle('');
            return;
        }
        
        setSelectedTemplate(tpl);
        setTitle(tpl.name);
        
        // Si pas de questionnaire lié, on applique direct !
        if (!tpl.linked_form_id) {
            applyTemplate(tpl, null);
        } else {
            // S'il y a un lien, on charge le texte brut et on attend qu'elle choisisse la note source
            setContent(tpl.content);
        }
    }

    // Fonction de "Publipostage" globale
    const applyTemplate = (tpl, sourceNote) => {
        let text = tpl.content
        const today = new Date().toLocaleDateString('fr-FR')
        const birth = patient.birth_date ? new Date(patient.birth_date).toLocaleDateString('fr-FR') : '...'
        
        text = text.replace(/{{nom}}/g, patient.last_name)
        text = text.replace(/{{prenom}}/g, patient.first_name)
        text = text.replace(/{{naissance}}/g, birth)
        text = text.replace(/{{date_jour}}/g, today)
        text = text.replace(/{{civilite}}/g, patient.gender === 'F' ? 'Mme' : patient.gender === 'M' ? 'M.' : '') 

        // Remplacement magique depuis le JSON (Fusion de TOUS les questionnaires) !
        if (sourceNote && sourceNote.form_data) {
            // On compile les champs de TOUS les modèles de formulaires existants
            let allFields = [];
            formTemplates.forEach(form => {
                try { allFields.push(...JSON.parse(form.fields_json || '[]')); } catch(e){}
            });
            
            let answers = {};
            try { answers = JSON.parse(sourceNote.form_data || '{}'); } catch(e){}

            // On cherche à remplacer n'importe quelle balise présente
            allFields.forEach(f => {
                const tag = `{{${sanitizeFieldName(f.label)}}}`;
                
                // Si la balise est dans le texte, on tente de la remplacer
                if (text.includes(tag)) {
                    const rawAns = answers[f.id];
                    let valStr = '___';
                    
                    if (rawAns) {
                        if (Array.isArray(rawAns)) {
                            // C'est un Répéteur : on en fait une vraie liste à puces HTML !
                            const validAnswers = rawAns.filter(Boolean);
                            valStr = validAnswers.length > 0 ? '<ul><li>' + validAnswers.join('</li><li>') + '</li></ul>' : '___';
                        } else {
                            // C'est un texte long : on remplace les \n par de vrais retours à la ligne HTML
                            valStr = String(rawAns).replace(/\n/g, '<br/>');
                        }
                    }
                    
                    const regex = new RegExp(tag, 'g');
                    text = text.replace(regex, valStr);
                }

            });
        }

        setContent(text)
    }

    const handleGenerate = async () => {
        if (!title || !content) return toast.error("Le document doit avoir un titre et du contenu")

        setLoading(true)
        try {
            // On envoie le HTML au backend qui fera le PDF
            await api.post(`/api/documents/patient/${patient.id}/generate`, {
                title: title,
                html_content: content,
                category: 'courrier',
                show_header: showHeader
            })
            
            toast.success("Document généré et enregistré !")
            localStorage.removeItem('bellablue_doc_title_backup')
            localStorage.removeItem('bellablue_doc_content_backup')
            onSuccess() // Pour rafraîchir la liste
            onClose()
        } catch (error) {

            console.error(error)
            toast.error("Erreur lors de la génération")
        } finally {
            setLoading(false)
        }
    }

    // Configuration de la barre d'outils de l'éditeur
    const modules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['clean']
        ],
    }

    const handleDraftWithAI = async () => {
        const note = notes.find(n => n.id === parseInt(selectedSourceNoteId));
        if (!note) return;

        setIsGeneratingAI(true);
        const toastId = toast.loading("Gaston rédige le courrier...");
        
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/ai/patient/${patient.id}/draft-letter`, {
                method: "POST",
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    note_content: note.content,
                    document_title: title || "Compte rendu de consultation"
                })
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.detail || "Erreur serveur");
            }

            // On efface le contenu actuel pour écrire le nouveau
            setContent("");
            toast.success("Rédaction en cours...", { id: toastId });


            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let done = false;
            let fullText = "";

            while (!done) {
                const { value, done: readerDone } = await reader.read();
                done = readerDone;
                if (value) {
                    const chunk = decoder.decode(value, { stream: true });
                    fullText += chunk;
                    // ReactQuill a besoin d'un formatage minimal (retours à la ligne HTML) pour que le streaming soit propre
                    setContent(fullText.replace(/\n/g, '<br/>'));
                }
            }
        } catch (error) {
            toast.error(error.message || "Erreur de communication avec Gaston", { id: toastId });
        } finally {
            setIsGeneratingAI(false);
        }
    };


    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col h-[90vh]">
                
                {/* Header */}
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-2xl">
                    <h3 className="font-serif font-bold text-xl text-gray-800 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-sage-600"/> Générateur de Document
                    </h3>
                    <button onClick={onClose}><X className="w-6 h-6 text-gray-400 hover:text-gray-600" /></button>
                </div>

                {/* Body */}
                <div className="flex-1 flex overflow-hidden">
                    
                    {/* Sidebar Modèles */}
                    <div className="w-64 bg-gray-50 border-r border-gray-100 p-4 overflow-y-auto hidden md:block">
                        <p className="text-xs font-bold text-gray-400 uppercase mb-3">Modèles disponibles</p>
                        <div className="space-y-2">
                            {/* Ajout manuel d'un modèle vierge si tu veux */}
                            <button 
                                onClick={() => { setContent(''); setTitle(''); }}
                                className="w-full text-left p-3 rounded-lg text-sm bg-white border border-gray-200 hover:border-sage-300 transition-all text-gray-400 italic"
                            >
                                Vide / Nouveau
                            </button>

                            {templates.map(tpl => (
                                <button 
                                    key={tpl.id}
                                    onClick={() => handleSelectTemplate(tpl)}
                                    className={`w-full text-left p-3 rounded-lg text-sm transition-all group border ${selectedTemplate?.id === tpl.id ? 'bg-sage-50 border-sage-400' : 'bg-white border-gray-200 hover:border-sage-300'}`}
                                >
                                    <span className="font-medium text-gray-700 group-hover:text-sage-700">{tpl.name}</span>
                                    <div className="text-xs text-gray-400 mt-1 uppercase">{tpl.category}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Zone Édition */}
                    <div className="flex-1 p-6 flex flex-col overflow-y-auto">
                        
                        {/* SELECTEUR DE SOURCE (Toujours visible pour l'IA et l'Injection) */}
                        <div className="mb-6 bg-indigo-50 border border-indigo-100 p-4 rounded-xl flex items-end gap-4 animate-in fade-in shadow-sm">
                            <div className="flex-1">
                                <label className="text-xs font-bold text-indigo-800 uppercase block mb-2">Consultation source (Pour Gaston ou Injection Auto)</label>
                                <select 
                                    value={selectedSourceNoteId}
                                    onChange={e => setSelectedSourceNoteId(e.target.value)}
                                    className="w-full text-sm border-indigo-200 bg-white"
                                >
                                    <option value="">Choisissez la consultation à utiliser...</option>
                                    {/* On affiche toutes les notes pour Gaston (mais on signale celles qui ont des données structurées) */}
                                    {notes.map(n => (
                                        <option key={n.id} value={n.id}>
                                            Consultation du {new Date(n.date).toLocaleDateString('fr-FR')} {n.form_data && n.form_data !== '{}' ? ' (Contient des cases cochées)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className="flex flex-col gap-2">
                                {/* Le bouton automatique n'apparaît QUE si le modèle est lié à un formulaire */}
                                {selectedTemplate?.linked_form_id && (
                                    <button 
                                        onClick={() => {
                                            const note = notes.find(n => n.id === parseInt(selectedSourceNoteId));
                                            if (note) applyTemplate(selectedTemplate, note);
                                        }}
                                        disabled={!selectedSourceNoteId || isGeneratingAI}
                                        className="btn bg-orange-500 hover:bg-orange-600 text-white shadow-sm disabled:opacity-50 h-[38px] text-sm whitespace-nowrap"
                                    >
                                        Appliquer les données (Auto)
                                    </button>
                                )}
                                
                                {/* LE BOUTON MAGIQUE GASTON (Toujours là !) */}
                                <button 
                                    onClick={handleDraftWithAI}
                                    disabled={!selectedSourceNoteId || isGeneratingAI}
                                    className="btn bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-sm disabled:opacity-50 h-[38px] text-sm whitespace-nowrap flex items-center justify-center gap-2"
                                    title="Gaston lira la note et rédigera le texte librement."
                                >
                                    {isGeneratingAI ? <div className="spinner w-4 h-4 border-white" /> : <Wand2 className="w-4 h-4" />}
                                    Rédiger avec l'IA (Beta)
                                </button>
                            </div>
                        </div>

                        <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">

                            <div className="flex-1 w-full">
                                <label className="text-xs font-bold text-gray-500 uppercase">Titre du document</label>
                                <input 
                                    value={title}
                                    onChange={e => setTitle(e.target.value)}
                                    className="w-full mt-1 font-bold text-lg"
                                    placeholder="Ex: Lettre Dr Dupont"
                                />
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer bg-white p-2 rounded-lg border border-gray-200 shadow-sm whitespace-nowrap mb-1">
                                <input 
                                    type="checkbox" 
                                    checked={showHeader} 
                                    onChange={(e) => setShowHeader(e.target.checked)}
                                    className="w-4 h-4 text-sage-600 rounded focus:ring-sage-500 border-gray-300"
                                />
                                <span className="text-xs font-bold text-gray-700">Imprimer l'en-tête (Logo & Contact)</span>
                            </label>
                        </div>

                        <div className="flex-1 flex flex-col">
                             <label className="text-xs font-bold text-gray-500 uppercase mb-1">Contenu</label>
                             <ReactQuill 
                                theme="snow" 
                                value={content} 
                                onChange={setContent} 
                                modules={modules}
                                className="h-full flex-1" 
                                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                             />
                             {/* Petit hack CSS pour que Quill prenne toute la hauteur : 
                                 .ql-container { flex: 1; overflow-y: auto; } 
                             */}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex justify-end gap-3">
                    <button onClick={() => {
                        localStorage.removeItem('bellablue_doc_title_backup');
                        localStorage.removeItem('bellablue_doc_content_backup');
                        onClose();
                    }} className="btn bg-white border border-gray-300 text-gray-600">Annuler</button>
                    <button onClick={handleGenerate} disabled={loading || !content} className="btn btn-primary flex items-center gap-2">
                        {loading ? <div className="spinner w-4 h-4 text-white"/> : <><Wand2 className="w-4 h-4" /> Générer le PDF</>}
                    </button>
                </div>
            </div>
        </div>
    )
}